import {
  authUserSchema,
  guardianRequestsResponseSchema,
  healthResponseSchema,
  profileResponseSchema,
  skillsResponseSchema,
  type AuthTokens,
  type AuthUser,
  type GuardianRequest,
  type HealthResponse,
  type ProfileResponse,
  type Skill,
  type UpdateProfileInput,
} from '@teentack/shared';

/** Relative by default so the Vite dev proxy handles it; absolute in deploys. */
export const API_BASE_URL = import.meta.env.VITE_API_BASE_URL ?? '/api';

const REFRESH_STORAGE_KEY = 'teentack.web.refreshToken';

export class ApiError extends Error {
  readonly status: number;
  readonly code: string | undefined;
  readonly details: unknown;

  constructor(status: number, message: string, code?: string, details?: unknown) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.code = code;
    this.details = details;
  }
}

export function buildApiUrl(path: string): string {
  const base = API_BASE_URL.replace(/\/+$/, '');
  const suffix = path.startsWith('/') ? path : `/${path}`;
  return `${base}${suffix}`;
}

/* -------------------------------------------------------------------------- */
/* Token store                                                                */
/* -------------------------------------------------------------------------- */

/**
 * The access token is held in memory only — it is short-lived and keeping it
 * out of storage limits the blast radius of an XSS bug. The refresh token has
 * to survive a reload, so it lives in localStorage.
 */
let accessToken: string | null = null;

export const tokenStore = {
  getAccessToken(): string | null {
    return accessToken;
  },
  getRefreshToken(): string | null {
    return localStorage.getItem(REFRESH_STORAGE_KEY);
  },
  set(tokens: AuthTokens): void {
    accessToken = tokens.accessToken;
    localStorage.setItem(REFRESH_STORAGE_KEY, tokens.refreshToken);
  },
  clear(): void {
    accessToken = null;
    localStorage.removeItem(REFRESH_STORAGE_KEY);
  },
  hasSession(): boolean {
    return localStorage.getItem(REFRESH_STORAGE_KEY) !== null;
  },
};

/* -------------------------------------------------------------------------- */
/* Fetch                                                                      */
/* -------------------------------------------------------------------------- */

interface FetchOptions extends RequestInit {
  /** Attach the bearer token. Defaults to true. */
  auth?: boolean;
  /** Internal — prevents an infinite refresh loop. */
  retryOnUnauthorized?: boolean;
}

async function rawFetch<T>(path: string, options: FetchOptions = {}): Promise<T> {
  const { auth = true, retryOnUnauthorized = true, headers, ...init } = options;

  const response = await fetch(buildApiUrl(path), {
    ...init,
    headers: {
      'Content-Type': 'application/json',
      ...(auth && accessToken ? { Authorization: `Bearer ${accessToken}` } : {}),
      ...headers,
    },
  });

  if (response.status === 204) return undefined as T;

  const payload: unknown = await response.json().catch(() => null);

  if (!response.ok) {
    const error = (payload as { error?: { message?: string; code?: string; details?: unknown } })
      ?.error;

    // One transparent refresh attempt, then give up and surface the 401.
    if (response.status === 401 && auth && retryOnUnauthorized && tokenStore.getRefreshToken()) {
      const refreshed = await tryRefresh();
      if (refreshed) {
        return rawFetch<T>(path, { ...options, retryOnUnauthorized: false });
      }
    }

    throw new ApiError(
      response.status,
      error?.message ?? response.statusText,
      error?.code,
      error?.details,
    );
  }

  return payload as T;
}

/**
 * The one in-flight refresh, shared by every caller.
 *
 * The api rotates the refresh token on use: the old one stops working the
 * moment a new pair is issued. Without this, a page that fires several queries
 * at once would send the same refresh token N times — the first would succeed
 * and the rest would fail against an already-rotated token, clear the store,
 * and log out a perfectly valid session. Collapsing them onto one promise means
 * the token is only ever spent once.
 */
let inFlightRefresh: Promise<AuthUser | null> | null = null;

async function performRefresh(): Promise<AuthUser | null> {
  const refreshToken = tokenStore.getRefreshToken();
  if (!refreshToken) return null;

  try {
    const result = await rawFetch<{ user: unknown; tokens: AuthTokens }>('/auth/refresh', {
      method: 'POST',
      body: JSON.stringify({ refreshToken }),
      auth: false,
      retryOnUnauthorized: false,
    });
    tokenStore.set(result.tokens);
    return authUserSchema.parse(result.user);
  } catch {
    // Only a genuinely rejected refresh clears the session — and because this
    // runs once, a rotated-token race can no longer trigger it.
    tokenStore.clear();
    return null;
  }
}

function refreshOnce(): Promise<AuthUser | null> {
  inFlightRefresh ??= performRefresh().finally(() => {
    inFlightRefresh = null;
  });
  return inFlightRefresh;
}

async function tryRefresh(): Promise<boolean> {
  return (await refreshOnce()) !== null;
}

export const apiFetch = rawFetch;

export function post<T>(path: string, body: unknown, options: FetchOptions = {}): Promise<T> {
  return rawFetch<T>(path, { ...options, method: 'POST', body: JSON.stringify(body) });
}

/* -------------------------------------------------------------------------- */
/* Endpoints                                                                  */
/* -------------------------------------------------------------------------- */

/** Parsed with the schema from @teentack/shared — one contract, three apps. */
export async function fetchHealth(): Promise<HealthResponse> {
  return healthResponseSchema.parse(await rawFetch<unknown>('/health', { auth: false }));
}

export async function fetchCurrentUser(): Promise<AuthUser> {
  const result = await rawFetch<{ user: unknown }>('/auth/me');
  return authUserSchema.parse(result.user);
}

/**
 * Restores a session from the stored refresh token. Shares the single in-flight
 * refresh with the 401 retry path, so a reload that mounts several queries at
 * once still spends the rotating token exactly once. [FR-M01.6]
 */
export function refreshSession(): Promise<AuthUser | null> {
  return refreshOnce();
}

/** The public skills catalogue that backs the signup skill picker. */
export async function fetchSkills(): Promise<Skill[]> {
  const result = await rawFetch<unknown>('/skills', { auth: false });
  return skillsResponseSchema.parse(result).skills;
}

/* -------------------------------------------------------------------------- */
/* Profile [FR-M03.1]                                                         */
/* -------------------------------------------------------------------------- */

/** The signed-in account's own profile. The id comes from the token. */
export async function fetchProfile(): Promise<ProfileResponse> {
  return profileResponseSchema.parse(await rawFetch<unknown>('/profile'));
}

/** Partial update. Returns the same envelope as the GET, already reloaded. */
export async function saveProfile(input: UpdateProfileInput): Promise<ProfileResponse> {
  const result = await rawFetch<unknown>('/profile', {
    method: 'PATCH',
    body: JSON.stringify(input),
  });
  return profileResponseSchema.parse(result);
}

/* -------------------------------------------------------------------------- */
/* Guardian approval [FR-M01.2]                                               */
/* -------------------------------------------------------------------------- */

/** Teens waiting on the signed-in parent. Parent accounts only. */
export async function fetchGuardianRequests(): Promise<GuardianRequest[]> {
  const result = await rawFetch<unknown>('/onboarding/guardian/pending');
  return guardianRequestsResponseSchema.parse(result).requests;
}

export async function approveGuardianRequest(relationshipId: string): Promise<void> {
  await rawFetch<void>(`/onboarding/guardian/${relationshipId}/approve`, { method: 'POST' });
}

export async function rejectGuardianRequest(relationshipId: string): Promise<void> {
  await rawFetch<void>(`/onboarding/guardian/${relationshipId}/reject`, { method: 'POST' });
}
